import React from 'react'

interface LaurelBadgeProps {
  /** 3 lines of condensed uppercase text inside the wreath */
  lines: [string, string, string]
  /** Play the stroke draw-in + leaves fade-in (Polish Plan item 5) */
  animate?: boolean
}

const CX = 100
const CY = 105
const R = 80

const polar = (deg: number) => {
  const rad = (deg * Math.PI) / 180
  return { x: CX + R * Math.cos(rad), y: CY + R * Math.sin(rad) }
}

/** Laurel branch: stem arc + leaves placed along it. Angles in SVG coords (y down). */
const Branch: React.FC<{ from: number; to: number; flip: boolean }> = ({ from, to, flip }) => {
  const start = polar(from)
  const end = polar(to)
  const sweep = flip ? 0 : 1
  const leaves = []
  const count = 9
  for (let i = 0; i <= count; i++) {
    const deg = from + ((to - from) * i) / count
    const p = polar(deg)
    // Leaves tilt outward from the stem, alternating slightly
    const rot = deg + (flip ? -55 : 55) + (i % 2 === 0 ? 8 : -8)
    leaves.push(
      <ellipse
        key={i}
        cx={p.x}
        cy={p.y}
        rx={11}
        ry={4.2}
        transform={`rotate(${rot} ${p.x} ${p.y})`}
        fill="currentColor"
      />
    )
  }
  return (
    <g>
      <path
        d={`M ${start.x} ${start.y} A ${R} ${R} 0 0 ${sweep} ${end.x} ${end.y}`}
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        pathLength={1}
        className="laurel-stem"
      />
      <g className="laurel-leaves">{leaves}</g>
    </g>
  )
}

/** Laurel-wreath award badge (Parity V2 A.2) — OG-style white laurels. */
const LaurelBadge: React.FC<LaurelBadgeProps> = ({ lines, animate = false }) => {
  return (
    <div
      className={`relative w-[140px] md:w-[150px] lg:w-[145px] xl:w-[155px] text-white/80 hover:text-accent-gold transition-all duration-500 hover:-translate-y-1 ${
        animate ? 'laurel-animate' : ''
      }`}
    >
      <svg viewBox="0 0 200 220" className="w-full h-auto" aria-hidden="true">
        {/* Left branch: bottom (105°) curving up to upper-left (200°) */}
        <Branch from={105} to={198} flip={false} />
        {/* Right branch: bottom (75°) curving up to upper-right (-18°) */}
        <Branch from={75} to={-18} flip={true} />
        {/* Bow at the bottom where branches meet */}
        <circle cx={CX} cy={CY + R + 2} r={4} fill="currentColor" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
        {lines.map((line, i) => (
          <span
            key={i}
            className={`font-display uppercase leading-snug tracking-[0.15em] ${
              i === 1 ? 'text-[13px] md:text-[14px] font-bold text-accent-gold' : 'text-[10px] md:text-[11px] font-medium'
            }`}
          >
            {line}
          </span>
        ))}
      </div>
    </div>
  )
}

export default LaurelBadge
